import { ChassisAbilities } from '@components/arsenal/chassis/ChassisAbilities';
import { UpgradePrestige } from '@components/arsenal/upgrade/UpgradePrestige';
import { ChassisLevel } from '@components/arsenal/chassis/ChassisLevel';
import { ChassisHeader } from '@components/arsenal/chassis/ChassisInfo';
import { UpgradeTutorial } from '@components/tutorial/UpgradeTutorial';
import { WeaponLevel } from '@components/arsenal/weapons/WeaponLevel';
import { WeaponHeader } from '@components/arsenal/weapons/WeaponInfo';
import { EquippedMod } from '@components/arsenal/mods/EquippedMod';
import { useUnequipModMutation, useEquipModMutation } from '@http';
import { IconAlertTriangle, IconPlus } from '@tabler/icons-react';
import { StatsDisplay } from '@components/arsenal/StatsDisplay';
import { ArsenalBack } from '@components/arsenal/ArsenalBack';
import { Mod } from '@components/arsenal/mods/Mod';
import { Button } from '@components/ui/Button';
import { useTranslation } from 'react-i18next';
import { Modal } from '@components/ui/Modal';
import { combineMods } from '@helpers/mods';
import { useAppStore } from '@stores';
import * as Shared from '@shared/lib';
import { useState } from 'react';

interface UpgradeProps {
  value: string;
  type: 'chassis' | 'weapon';
  onBack?: () => void;
};

export function Upgrade({ value, type, onBack }: UpgradeProps) {
  const [showPrestige, setShowPrestige] = useState(false);
  const [selectMod, setSelectMod] = useState<number | null>(null);

  const { t } = useTranslation(undefined, { keyPrefix: 'Upgrade' });
  const { mutateAsync: unequipModAsync } = useUnequipModMutation();
  const { mutateAsync: equipModAsync } = useEquipModMutation();
  const { userData, refetch, setIsFetching } = useAppStore();

  const target = 
    userData.chassis.find((c) => c.key === value)
    ?? userData.weapons.find((w) => w.key === value);
  if (!target) return null;

  const level = type === 'chassis'
    ? Shared.ChassisLevelCalculator.calc(target.xp).current
    : Shared.WeaponLevelCalculator.calc(target.xp).current;
  const maxXp = type === 'chassis'
    ? Shared.ChassisLevelCalculator.getXpForLevel(30)
    : Shared.WeaponLevelCalculator.getXpForLevel(30);
  const canUpgrade = target.xp >= maxXp;
  const availableMods = userData.mods
    .filter(m => !target.mods.map((m) => m.mod.key).includes(m.key))
    .filter(m => Shared.modData.some((d) => d.key === m.key && d.type === type));
  const combinedMods = combineMods(availableMods, userData);

  const equipMod = async (modId: string) => {
    const slot = selectMod;
    if (slot === null) return;

    setIsFetching(true);
    await equipModAsync({
      targetId: target.id,
      id: modId,
      slot
    });
    refetch();
    setSelectMod(null);
  };

  const removeMod = async (id: string) => {
    setIsFetching(true);
    await unequipModAsync({
      targetId: target.id,
      id,
    });
    refetch();
  };

  const ModSlot = ({ slot }: { slot: number }) => {
    const equippedId = target.mods.find((m) => m.slot === slot)?.modId;

    if (!equippedId) return (
      <button className={`${slot < 5 && 'mb-2'} mod-slot tutorial-slot-${slot}`} onClick={() => setSelectMod(slot)}>
        <IconPlus className="text-gray-500" />
      </button>
    );

    const mod = userData.mods.find((m) => m.id === equippedId);
    if (!mod) return null;

    return (
      <div className={slot < 5 ? 'mb-2' : ''}>
        <EquippedMod
          onRemove={() => removeMod(mod.id)}
          isOverCapacity={isOverCapacity}
          mod={mod}
        />
      </div>
    );
  };

  const usedEnergy = target.mods.reduce((acc, mod) => {
    const data = userData.mods.find((m) => m.id === mod.modId);
    if (!data) return acc;
    return acc + data.level + 1;
  }, 0);

  const isOverCapacity = usedEnergy > level + target.prestige;

  return (
    <>
      <UpgradeTutorial />
      <UpgradePrestige
        onClose={() => setShowPrestige(false)}
        open={showPrestige}
        id={target.id}
        type={type}
      />

      <Modal onRequestClose={() => setSelectMod(null)} isOpen={selectMod !== null} title={t('selectMod')}>
        {combinedMods.length === 0 && (
          <p className="text-center text-sm text-gray-300 p-2 mt-2">
            {t('noMods')}
          </p>
        )}

        <div className="grid grid-cols-2 gap-2 p-2">
          {combinedMods.map((mod) => (
            <Mod onClick={() => equipMod(mod.id)} count={mod.count} key={mod.id} mod={mod} />
          ))}
        </div>
      </Modal>

      {onBack && (
        <ArsenalBack bg="chassis.avif" onClick={onBack} />
      )}

      {type === 'weapon' && <WeaponHeader weapon={value} />}
      {type === 'chassis' && <ChassisHeader chassis={value} />}

      <StatsDisplay compareId={target.id} value={value} type={type} />

      {type === 'chassis' && (
        <>
          <p className="mt-4 text-lg px-2">{t('abilities')}</p>
          <ChassisAbilities chassis={value} />
        </>
      )}

      <div className="level-tutorial">
        <p className="mt-4 text-lg px-2">{t('level')}</p>
        <div className="border-y border-secondary p-2 grid-overlay mt-1">
          {type === 'chassis' && <ChassisLevel xp={target.xp} />}
          {type === 'weapon' && <WeaponLevel xp={target.xp} />}

          <Button
            onClick={() => setShowPrestige(true)}
            className="mt-2 btn-active"
            disabled={!canUpgrade}
          >
            {t(canUpgrade ? 'evolve' : 'cantEvolve')}
          </Button>
        </div>
      </div>

      <div className="mod-tutorial">
        <div className="flex text-center justify-between mt-6 px-2">
          <p className="text-lg">{t('mods')}</p>
          <div className="flex items-center">
            {isOverCapacity && <IconAlertTriangle className="text-red-500 mr-1 mt-[1px]" size={13} />}
            <p className={`${isOverCapacity ? 'text-red-500' : 'text-lime-400'} font-bold text-sm`}>
              {usedEnergy}{' / '}
              {level}
              {target.prestige > 0 && ` + ${target.prestige}`}
            </p>
          </div>
        </div>
        <div className="border-y border-secondary p-2 grid-overlay mt-1">
          {Array.from({ length: 6 }).map((_, i) => (
            <ModSlot slot={i} key={i} />
          ))}
        </div>
      </div>
    </>
  );
}